/* eslint-disable array-callback-return */
import React, { Fragment } from 'react';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';
import { Wrapper, TableContainer, Table } from './style';
import {
  HOOD,
  APERTURE,
  COLOR,
  PRINT,
  DESIGN,
  PANELS,
  PACK,
  SIZE,
  BASE_COLOR,
  LID_COLOR,
  STREAM,
  QUANTITY,
} from './filterNames';
import { chunk, flatten, toTitleCase } from '../../../common/functions';
import darkIcons from '../../../../images/stream-icons-dark/index';
import { COLOR_NAME_MAP, STREAMS, Button } from '../../../common/variables';
import images from '../../../../images/';

const ShowMoreButton = styled(Button)`
  width: 90%;
  margin: 20px auto;
`;

class ProductPicker extends React.Component {
  state = {
    page: 0,
    filters: {},
    filtersTitle: [],
    tableSize: 10,
    showMoreIndex: 0,
  };

  generateFilterName = (filter) => {
    switch (filter) {
      case SIZE:
        return 'Size';
      case BASE_COLOR:
        return 'Base Color';
      case LID_COLOR:
        return 'Lid Color';
      case STREAM:
        return 'Stream';
      case QUANTITY:
        return 'Quantity';
      case HOOD:
        return 'Hood';
      case APERTURE:
        return 'Aperture';
      case COLOR:
        return 'Color';
      case PRINT:
        return 'Print';
      case DESIGN:
        return 'Design';
      case PANELS:
        return 'Panels';
      case PACK:
        return 'Pack';
      default:
        console.error('[Alert] No corresponding filter name for ', filter);
        return filter;
    }
  };

  renderFilterOption = (name, value) => {
    if (
      typeof value === 'undefined' ||
      typeof name === 'undefined' ||
      !name ||
      !value
    )
      return 'N/A';

    switch (name) {
      case SIZE:
        return `${value} Litres`;
      case STREAM:
        if (STREAMS.find((s) => s.slug === value)) {
          return STREAMS.find((s) => s.slug === value).name
        }
        else {
          return 'N/A';
        }
      case undefined:
      case '':
      default:
        return toTitleCase(value);
    }
  };

  // This will render the value in the Table Cell,
  // for example, render "15" as "15 Litres" if its VOLUME
  renderFilters = (value, key) => {
    if (typeof value === 'undefined') return 'N/A';
    switch (key) {
      case SIZE:
        return `${value} Litres`;
      case QUANTITY:
        return value;
      case BASE_COLOR:
      case LID_COLOR:
      case COLOR:
        return (
          <div className="colour-result-container">
            <div
              style={{
                background: value,
                width: value === '#ffffff' ? 20 : 22,
                height: value === '#ffffff' ? 20 : 22,
                borderRadius: 15,
                marginRight: 15,
                border: value === '#ffffff' ? '1px solid #808080' : 0,
              }}
            />
            {COLOR_NAME_MAP[value] || value}
          </div>
        );
      case STREAM:
        return value.map((stream) => {
          return (
            <div className="stream-result-container" key={stream.slug}>
              {darkIcons[STREAMS.find((s) => s.slug === stream.slug)] && (
                <SVG
                  title={stream.slug}
                  className="inline-picker-svg"
                  src={
                    darkIcons[STREAMS.find((s) => s.slug === stream.slug).id]
                      .default
                  }
                />
              )}
              {STREAMS.find((s) => s.slug === stream.slug) && (
                <div className="stream-name">
                  {toTitleCase(
                    STREAMS.find((s) => s.slug === stream.slug).name
                  )}
                </div>
              )}
            </div>
          );
        });
      case undefined:
      case '':
        return 'N/A';

      default:
        return value || 'N/A';
    }
  };

  handleFilterChange = (event) => {
    const { value, name } = event.target;
    // If the new value is the 'default' value, wipe that filter.
    if (event.target.value === 'default') {
      const newFilters = { ...this.state.filters };
      if (newFilters[name]) delete newFilters[name];
      this.setState({ filters: newFilters });
    } else {
      // Set their value selection from the drop down as the
      // new filter for that field.
      this.setState({
        filters: { ...this.state.filters, [name]: value },
        page: 0,
      });
    }
  };

  filterProducts = (product) => {
    // Remove products whose values don't match the current filter state.
    return Object.keys(this.state.filters).every((filter) => {
      // If the filter is an array, we cant just compare
      // We need to check whether the selected filter
      // is contained in the filter array in the product.
      if (Array.isArray(product.flattedFilters[filter])) {
        return product.flattedFilters[filter].some(
          (item) => this.state.filters[filter] === item.slug
        );
      }

      // Return TRUE (dont filter it) if the value for the
      // selected filter matches the value of that filter
      // for the product.
      return (
        product.flattedFilters[filter] === this.state.filters[filter] ||
        this.state.filters[filter] === 'default'
      );
    });
  };

  renderFilterOptions = (name) => {
    // This will render the possible drop-down menu options for each column

    // This will store the values for this field.
    let values = [];

    // Filter products to only ones which match the current filters
    this.props.productRecords
      .filter(this.filterProducts)
      .filter((product) => {
        // We want to iterate over each product we have
        // and return the possible filter names of
        // products which could be selected based off
        // already selected filters.
        const productValues = product.flattenFilters;
        const filterValues = this.state.filters;

        // If theres no selected filters, just return everything.
        if (
          !filterValues ||
          Object.keys(filterValues) === 0 ||
          !productValues
        ) {
          return true;
        }

        // If there are selected filters, we want to
        // only return the ones which are equal to
        // their equivalent in the this.state.filters
        // object.
        if (
          Object.keys(filterValues).some(
            (filter) => filterValues[filter] !== productValues[filter]
          )
        ) {
          return false;
        }

        // If it hasnt returned false by now, just include it in the results.
        return true;
      })
      .map((product) => {
        const value = product.flattedFilters[name];
        if (Array.isArray(value)) {
          // If the value of this filter is an array, its
          // the streams filter and needs to be specially
          // handled
          values = [...values, ...value.map((s) => s.slug)];
          return;
        }

        // Push the value of the filter into the values.
        values.push(product.flattedFilters[name]);
      });

    // Return a unique array of possible values
    return (
      Array.from(new Set(values))
        // sort options by length then by alphabet
        .sort(function (a, b) {
          return a.length - b.length || a.localeCompare(b);
        })
        .map((v) => {
          // If the filter option is a color, render it with its' color
          if (!v) return null;
          if (v.startsWith('#')) {
            if (!COLOR_NAME_MAP[v])
              console.warn('[Alert] No human-readable name for color: ' + v);
            return (
              <option
                key={v}
                value={v}
                style={{
                  color: v === '#ffffff' ? 'grey' : v,
                }}
              >
                {/* Instead of rendering the hexadecimal value for the color,
              we will use a map to render its human-readable name. The
              colors must be manually added. Otherwise, it will just
              fallback to showing the hex value.*/}
                {COLOR_NAME_MAP[v] || v}
              </option>
            );
          }

          return (
            <option key={v} value={v}>
              {this.renderFilterOption(name, v)}
            </option>
          );
        })
    );
  };

  handlePageChange = (action) => {
    let { productRecords } = this.props;

    const chunkedProducts = chunk(
      productRecords.filter(this.filterProducts),
      parseInt(this.state.tableSize)
    );

    let returnPage = this.state.page;
    switch (action) {
      case 'NEXT':
        returnPage++;
        break;
      case 'PREVIOUS':
        returnPage--;
        break;
      case 'FIRST':
        returnPage = 0;
        break;
      case 'LAST':
        returnPage = chunkedProducts.length;
        break;
      default:
        console.error('[Alert] Unexpected action occured.');
    }

    this.setState({
      page: Math.min(Math.max(returnPage, 0), chunkedProducts.length - 1),
    });
  };

  setTableSize = (size) => {
    if (!['10', '25', '50', '100', 'All']) {
      console.error('[Alert] Invalid Table Size Given.');
    }

    if (size === 'All')
      size = this.props.productRecords.filter(this.filterProducts).length;
    this.setState({ tableSize: parseInt(size), page: 0 });
  };

  render() {
    let { productRecords, mobile } = this.props;
    productRecords = productRecords.filter(this.filterProducts);
    this.numRows = productRecords.length;
    const chunkedProducts = chunk(
      productRecords,
      parseInt(this.state.tableSize)
    );

    const mobileProducts = chunk(productRecords, 7);
    productRecords = chunkedProducts[this.state.page];
    // Get all filters name in table head

    let filtersTitle = {};

    this.props.productRecords.map((i) => {
      filtersTitle = { ...filtersTitle, ...i.flattedFilters };
    });
    return (
      <Wrapper>
        {mobile ? (
          <div className="stream-selector-mobile">
            <div className="select-container">
              {Object.keys(filtersTitle).map(
                (filterName) =>
                  filterName !== '__typename' && (
                    <div className="select-subcontainer" key={filterName}>
                      <select
                        className="stream-select"
                        name={filterName}
                        onChange={this.handleFilterChange}
                        value={this.state.filters[filterName] || 'default'}
                      >
                        <option value="default">
                          {this.generateFilterName(filterName)}
                        </option>
                        {this.renderFilterOptions(filterName)}
                      </select>
                    </div>
                  )
              )}
              {Object.keys(this.state.filters).length > 0 && (
                <div
                  className="remove-all-filters"
                  onClick={() => {
                    this.setState({ filters: {} });
                  }}
                >
                  Remove all Filters
                </div>
              )}
              {Object.keys(this.state.filters).length > 0 && (
                <div className="text-result">
                  SHOWING RESULTS FOR{' '}
                  {Object.keys(this.state.filters).map((key, ind) => {
                    let value = this.state.filters[key];
                    if (value.startsWith('#')) value = COLOR_NAME_MAP[value];
                    return (
                      <Fragment key={key}>
                        {this.generateFilterName(key).toUpperCase()}
                        {': '}
                        <span>{value.toUpperCase()}</span>
                        {ind + 1 !== Object.keys(this.state.filters).length &&
                          ', '}
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </div>
            {Array.isArray(mobileProducts) &&
              mobileProducts.length > 0 &&
              mobileProducts.slice(0, 1 + this.state.showMoreIndex).length >
                0 &&
              flatten(
                mobileProducts.slice(0, 1 + this.state.showMoreIndex)
              ).map((product) => (
                <div key={product.productCode} className="selected-view">
                  <div className="text-container">
                    <span className="product-code">
                      {product.productCode + ' '}
                    </span>
                    {product.desc}
                  </div>
                </div>
              ))}
            {mobileProducts.length > 1 &&
              this.state.showMoreIndex + 1 < mobileProducts.length && (
                <ShowMoreButton
                  onClick={() =>
                    this.setState({
                      showMoreIndex: this.state.showMoreIndex + 1,
                    })
                  }
                >
                  Show more
                </ShowMoreButton>
              )}
          </div>
        ) : (
          <TableContainer>
            <Table>
              <thead>
                <tr className="table-head-titles">
                  <th />
                  {Object.keys(filtersTitle).map(
                    (filterName) =>
                      filterName !== '__typename' && (
                        <th key={filterName}>
                          <p>{this.generateFilterName(filterName)}</p>
                        </th>
                      )
                  )}
                  <th />
                </tr>
                <tr>
                  <th className="heading-long">Product Code</th>
                  {Object.keys(filtersTitle).map(
                    (filterName) =>
                      filterName !== '__typename' && (
                        <th key={filterName}>
                          <div className="heading-table">
                            <select
                              className="stream-select"
                              name={filterName}
                              onChange={this.handleFilterChange}
                              value={
                                this.state.filters[filterName] || 'default'
                              }
                            >
                              <option value="default">Select</option>
                              {/* <option value="default">
                          {this.generateFilterName(filterName)}
                        </option> */}
                              {this.renderFilterOptions(filterName)}
                            </select>
                          </div>
                        </th>
                      )
                  )}

                  <th className="heading-long">Description</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(productRecords).map((key) => {
                  const product = productRecords[key];
                  return (
                    <tr key={product.productCode}>
                      <td className="product-code">{product.productCode}</td>

                      {Object.keys(filtersTitle).map(
                        (key) =>
                          product.flattedFilters[key] &&
                          product.flattedFilters[key].indexOf('Wp') === -1 && (
                            <td key={key} className="filter">
                              {this.renderFilters(
                                product.flattedFilters[key],
                                key
                              )}
                            </td>
                          )
                      )}
                      <td className="desc">{product.desc}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {this.props.productRecords.filter(this.filterProducts).length >
              10 && (
              <div className="pagination-container">
                <div className="pagination-side">
                  {/* Show 10 / 25 / 50 / 100 / All */}
                  <div>Show:</div>
                  {['10', '25', '50', '100', 'All'].map((i) => (
                    <Fragment key={i}>
                      <div
                        className="page-number"
                        key={i}
                        onClick={() => this.setTableSize(i)}
                        style={{
                          borderBottom:
                            this.state.tableSize == i ||
                            (!['10', '25', '50', '100'].includes(i) &&
                              this.state.tableSize == this.numRows)
                              ? '1px solid gray'
                              : 'none',
                          cursor: 'pointer',
                        }}
                      >
                        {i}
                      </div>
                      {i !== 'All' && '/'}
                    </Fragment>
                  ))}
                </div>
                {/*<< < Page X of X > >>*/}
                <div className="pagination-side">
                  <div className="pagination-left">
                    <img
                      onClick={() => this.handlePageChange('FIRST')}
                      className="pagination-arrow"
                      alt="First Page"
                      src={images.PAGINATION_ARROWS.FIRST.default}
                    />

                    <img
                      onClick={() => this.handlePageChange('PREVIOUS')}
                      className="pagination-arrow"
                      alt="Previous Page"
                      src={images.PAGINATION_ARROWS.PREVIOUS.default}
                    />
                  </div>

                  <div className="page-tracker-text">
                    Page {this.state.page + 1} of {chunkedProducts.length}
                  </div>

                  <div className="pagination-right">
                    <img
                      onClick={() => this.handlePageChange('NEXT')}
                      className="pagination-arrow"
                      alt="Next Page"
                      src={images.PAGINATION_ARROWS.NEXT.default}
                    />

                    <img
                      onClick={() => this.handlePageChange('LAST')}
                      className="pagination-arrow"
                      alt="Last Page"
                      src={images.PAGINATION_ARROWS.LAST.default}
                    />
                  </div>
                </div>
              </div>
            )}
            {Object.keys(this.state.filters).length > 0 && (
              <div
                className="remove-all-filters"
                onClick={() => {
                  this.setState({ filters: {} });
                }}
              >
                Remove all Filters
              </div>
            )}
          </TableContainer>
        )}
      </Wrapper>
    );
  }
}

export default ProductPicker;
